// Account + provider info (docs/product-specs/accounts.md) plus device
// preferences (mic / speaker / camera + audio processing). Provider
// configuration is env-driven on the server and read-only; device prefs
// are a client concern, persisted in localStorage (lib/devices).

import { html, LitElement, nothing } from "lit";
import {
  applySpeaker,
  type DeviceList,
  type DevicePrefs,
  listDevices,
  loadDevicePrefs,
  micConstraints,
  saveDevicePrefs,
} from "../lib/devices";
import { store } from "../lib/store";

export class MmSettings extends LitElement {
  private prefs: DevicePrefs = loadDevicePrefs();
  private devices: DeviceList = { mics: [], speakers: [], cameras: [] };
  private testing = false;
  private micLevel = 0;
  private notice = "";

  // Live-preview plumbing (only while `testing`).
  private previewStream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private raf = 0;

  override createRenderRoot(): HTMLElement {
    return this;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    store.subscribe(() => this.requestUpdate());
    void this.refreshDevices();
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.stopPreview();
  }

  private async refreshDevices(): Promise<void> {
    try {
      this.devices = await listDevices();
      this.requestUpdate();
    } catch {
      /* enumeration can fail before any permission — ignore */
    }
  }

  private savePref(patch: Partial<DevicePrefs>): void {
    this.prefs = saveDevicePrefs(patch);
    this.requestUpdate();
    // Re-open capture so a device/processing change is audible/visible.
    if (this.testing) void this.startPreview();
  }

  private async startPreview(): Promise<void> {
    this.stopPreview();
    this.notice = "";
    try {
      const wantsCamera = this.devices.cameras.length > 0;
      this.previewStream = await navigator.mediaDevices.getUserMedia({
        audio: micConstraints(undefined, this.prefs),
        video: wantsCamera
          ? { deviceId: this.prefs.cameraId ? { exact: this.prefs.cameraId } : undefined }
          : false,
      });
      this.testing = true;
      // Granting permission reveals device labels — re-enumerate.
      await this.refreshDevices();
      this.startMeter(this.previewStream);
      this.requestUpdate();
    } catch (e) {
      this.testing = false;
      this.notice =
        e instanceof Error ? e.message : "could not open the selected devices";
      this.requestUpdate();
    }
  }

  private startMeter(stream: MediaStream): void {
    if (stream.getAudioTracks().length === 0) return;
    this.audioCtx = new AudioContext();
    const src = this.audioCtx.createMediaStreamSource(stream);
    const analyser = this.audioCtx.createAnalyser();
    analyser.fftSize = 512;
    src.connect(analyser);
    const buf = new Float32Array(analyser.fftSize);
    const tick = (): void => {
      analyser.getFloatTimeDomainData(buf);
      let sum = 0;
      for (const v of buf) sum += v * v;
      const rms = Math.sqrt(sum / buf.length);
      this.micLevel = Math.min(1, rms * 6);
      this.requestUpdate();
      this.raf = requestAnimationFrame(tick);
    };
    tick();
  }

  private stopPreview(): void {
    this.testing = false;
    this.micLevel = 0;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.previewStream?.getTracks().forEach((t) => t.stop());
    this.previewStream = null;
    if (this.audioCtx && this.audioCtx.state !== "closed") void this.audioCtx.close();
    this.audioCtx = null;
    this.requestUpdate();
  }

  /** Play a short tone through the selected output to confirm it. */
  private async testSpeaker(): Promise<void> {
    try {
      const ctx = new AudioContext();
      const dest = ctx.createMediaStreamDestination();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = 440;
      gain.gain.value = 0.15;
      osc.connect(gain).connect(dest);
      const audio = new Audio();
      audio.srcObject = dest.stream;
      await applySpeaker(audio, this.prefs.speakerId);
      await audio.play();
      osc.start();
      setTimeout(() => {
        osc.stop();
        audio.pause();
        void ctx.close();
      }, 600);
    } catch {
      this.notice = "could not play a test tone on the selected speaker";
      this.requestUpdate();
    }
  }

  /// Attach the live preview stream to the <video> after each render.
  override updated(): void {
    const video = this.querySelector<HTMLVideoElement>("#device-preview");
    if (video && this.previewStream && video.srcObject !== this.previewStream) {
      video.srcObject = this.previewStream;
      void video.play().catch(() => {});
    }
  }

  private deviceSelect(
    label: string,
    kind: keyof DeviceList,
    field: "micId" | "speakerId" | "cameraId",
    fallback: string,
  ) {
    const list = this.devices[kind];
    const value = this.prefs[field];
    return html`
      <dt>${label}</dt>
      <dd>
        <select
          class="device-select"
          .value=${value}
          @change=${(e: Event) =>
            this.savePref({ [field]: (e.target as HTMLSelectElement).value } as Partial<DevicePrefs>)}
        >
          <option value="">${fallback}</option>
          ${list.map(
            (d) =>
              html`<option value=${d.deviceId} ?selected=${d.deviceId === value}>
                ${d.label || `${label} ${d.deviceId.slice(0, 6)}`}
              </option>`,
          )}
        </select>
      </dd>
    `;
  }

  private toggle(label: string, field: keyof DevicePrefs) {
    return html`<label class="device-toggle">
      <input
        type="checkbox"
        .checked=${this.prefs[field] as boolean}
        @change=${(e: Event) =>
          this.savePref({ [field]: (e.target as HTMLInputElement).checked } as Partial<DevicePrefs>)}
      />
      ${label}
    </label>`;
  }

  override render() {
    const { user, provider } = store.get();
    const speakerSelectable =
      typeof (HTMLMediaElement.prototype as { setSinkId?: unknown }).setSinkId === "function";
    const pct = Math.round(this.micLevel * 100);
    const labelsHidden =
      this.devices.mics.every((m) => !m.label) && !this.testing;
    return html`
      <section class="summary">
        <p class="eyebrow">Workspace</p>
        <h1>Settings</h1>
      </section>

      <section>
        <h2>Devices</h2>
        <p class="muted">
          Choose which microphone, speaker, and camera to use. These apply
          to the recorder and to hosted calls on this device, and are
          remembered in this browser.
        </p>
        ${labelsHidden
          ? html`<div class="message-card message-card-info">
              <p>Click “Test devices” to grant permission and see device names.</p>
            </div>`
          : nothing}
        <dl class="meta">
          ${this.deviceSelect("Microphone", "mics", "micId", "Default microphone")}
          ${this.deviceSelect(
            "Speaker",
            "speakers",
            "speakerId",
            speakerSelectable ? "Default speaker" : "Default speaker (system-controlled)",
          )}
          ${this.deviceSelect("Camera", "cameras", "cameraId", "Default camera")}
        </dl>

        <h3>Audio processing</h3>
        <div class="device-toggles">
          ${this.toggle("Echo cancellation", "echoCancellation")}
          ${this.toggle("Noise suppression", "noiseSuppression")}
          ${this.toggle("Auto gain control", "autoGainControl")}
        </div>

        <div class="toolbar" style="margin-top: 0.9rem">
          ${this.testing
            ? html`<button class="ghost-button" @click=${this.stopPreview}>
                ■ Stop test
              </button>`
            : html`<button class="primary-button" @click=${this.startPreview}>
                ● Test devices
              </button>`}
          <button
            class="ghost-button"
            ?disabled=${!speakerSelectable}
            title=${speakerSelectable
              ? "Play a test tone on the selected speaker"
              : "This browser controls audio output at the system level"}
            @click=${this.testSpeaker}
          >
            🔊 Test speaker
          </button>
          <button class="ghost-button" @click=${this.refreshDevices}>Refresh list</button>
        </div>

        ${this.testing
          ? html`
              <div class="device-preview">
                ${this.devices.cameras.length > 0
                  ? html`<video id="device-preview" muted playsinline></video>`
                  : html`<p class="muted">No camera detected.</p>`}
                <div>
                  <p class="muted" style="margin: 0 0 0.3rem">Microphone level</p>
                  <div class="level-meter">
                    <div
                      class="level-meter-fill ${pct > 85 ? "hot" : ""}"
                      style="width: ${pct}%"
                    ></div>
                  </div>
                </div>
              </div>
            `
          : nothing}
        ${this.notice
          ? html`<div class="message-card message-card-error"><p>${this.notice}</p></div>`
          : nothing}
      </section>

      <section>
        <h2>Account</h2>
        <dl class="meta">
          <dt>Email</dt>
          <dd><code>${user?.email ?? "—"}</code></dd>
          <dt>Inference provider</dt>
          <dd><span class="badge badge-accent">${provider || "unknown"}</span></dd>
        </dl>
      </section>

      <section>
        <h2>Privacy note</h2>
        <p class="muted">
          Audio is captured in your browser, trimmed to speech, and sent to
          this server, which forwards it to the configured inference
          provider for transcription and summarization. With a self-hosted
          OpenAI-compatible endpoint nothing leaves your infrastructure;
          with the Livepeer clearinghouse, audio is processed by network
          orchestrators. Provider selection is configured by the server
          operator (<code>PROVIDER_MODE</code>).
        </p>
      </section>
    `;
  }
}

customElements.define("mm-settings", MmSettings);
